import cn from 'classnames';
import { FormContext } from 'informed';
import { useTranslation } from 'react-i18next';

import { InformedCheckbox } from '@nw/design-system/omega';

import {
  enrollmentEscrowTermsAndConditionsLabel,
  enrollmentGatewayCampusChangePolicyLabel,
  enrollmentGatewayFinalityPolicyLabel,
  enrollmentGatewayTermsAndConditionsLabel,
  seatReservationCommunicationConsentLabel,
  seatReservationRefundPolicyLabel,
  seatReservationTermsAndConditionsLabel
} from '../../constants/PaymentTermsAndConditionsConstants';
import {
  defaultRefundPolicyLabel,
  defaultTermsAndConditionsLabel
} from '../../constants/TermsAndConditionsConstants';
import {
  ApplicationProductFieldValidationSchema,
  getPaymentTermsValidationSchema
} from '../../utils/userRoleDetailsAndApplicationProductValidationSchema';

import { FieldContainer } from '../ApplicationSectionsPage/styledComponents';

import { TermsAndConditionsCard } from './styledComponents';

export type PaymentTermsVariant =
  | 'default'
  | 'seatReservation'
  | 'enrollmentGateway'
  | 'enrollmentEscrow';

interface TermsAndConditionsProps {
  variant?: PaymentTermsVariant;
  termsAndConditionsLabel?: string;
  refundPolicyLabel?: string;
  campusChangeAcknowledgementLabel?: string;
  showCampusChangeAcknowledgement?: boolean;
  withBackgroundCard?: boolean;
}

export const TermsAndConditions = (
  formProps: FormContext,
  termsAndConditionsProps?: TermsAndConditionsProps
): React.ReactNode => {
  const { t } = useTranslation();
  const {
    variant = 'default',
    termsAndConditionsLabel = defaultTermsAndConditionsLabel,
    refundPolicyLabel = defaultRefundPolicyLabel,
    campusChangeAcknowledgementLabel = '',
    showCampusChangeAcknowledgement = false,
    withBackgroundCard = false
  } = termsAndConditionsProps ?? {};

  const validationSchemaObject =
    variant === 'default'
      ? ApplicationProductFieldValidationSchema(t)
      : getPaymentTermsValidationSchema(t, variant);

  const renderCheckBoxField = (
    formPropsContext: FormContext,
    field: string,
    label: string,
    shouldShowOnlyLabel = false
  ) => {
    const { formState } = formPropsContext;
    const { touched, errors } = formState;
    return (
      <FieldContainer>
        <InformedCheckbox
          field={field}
          key={field}
          label={label}
          error={errors[field]}
          touched={touched[field]}
          shouldShowOnlyLabel={shouldShowOnlyLabel}
          validationSchemaObject={validationSchemaObject}
          isInnerHTML={true}
          validateOnBlur={false}
          labelClassName={cn('informedInputLabelCheckboxText')}
          isMarginBottomZero={true}
        />
      </FieldContainer>
    );
  };

  const renderDefaultTermsContent = () => (
    <>
      {renderCheckBoxField(
        formProps,
        'communicationConsent',
        'I consent to receiving communications from NxtWave.'
      )}
      {showCampusChangeAcknowledgement
        ? renderCheckBoxField(
          formProps,
          'nxtwaveCampusChangePolicy',
          campusChangeAcknowledgementLabel
        )
        : null}
      {renderCheckBoxField(
        formProps,
        'nxtwaveTermsAndConditions',
        termsAndConditionsLabel
      )}
      {renderCheckBoxField(
        formProps,
        'nxtwaveRefundPolicy',
        refundPolicyLabel,
        true
      )}
    </>
  );

  const renderSeatReservationTermsContent = () => (
    <>
      {renderCheckBoxField(
        formProps,
        'nxtwaveTermsAndConditions',
        seatReservationTermsAndConditionsLabel
      )}
      {renderCheckBoxField(
        formProps,
        'communicationConsent',
        seatReservationCommunicationConsentLabel
      )}
      {renderCheckBoxField(
        formProps,
        'nxtwaveRefundPolicy',
        seatReservationRefundPolicyLabel
      )}
    </>
  );

  const renderEnrollmentGatewayTermsContent = () => (
    <>
      {renderCheckBoxField(
        formProps,
        'nxtwaveTermsAndConditions',
        enrollmentGatewayTermsAndConditionsLabel
      )}
      {renderCheckBoxField(
        formProps,
        'nxtwaveEnrollmentCampusChangePolicy',
        enrollmentGatewayCampusChangePolicyLabel
      )}
      {renderCheckBoxField(
        formProps,
        'nxtwaveEnrollmentFinalityPolicy',
        enrollmentGatewayFinalityPolicyLabel
      )}
    </>
  );

  const renderEnrollmentEscrowTermsContent = () =>
    renderCheckBoxField(
      formProps,
      'nxtwaveTermsAndConditions',
      enrollmentEscrowTermsAndConditionsLabel
    );

  const termsContent = (() => {
    switch (variant) {
      case 'seatReservation':
        return renderSeatReservationTermsContent();
      case 'enrollmentGateway':
        return renderEnrollmentGatewayTermsContent();
      case 'enrollmentEscrow':
        return renderEnrollmentEscrowTermsContent();
      default:
        return renderDefaultTermsContent();
    }
  })();

  if (withBackgroundCard) {
    return <TermsAndConditionsCard>{termsContent}</TermsAndConditionsCard>;
  }

  return termsContent;
};